import React, { useEffect, useRef, useState } from 'react';
import './App.css';

/* =========================================================
   DATA
   ========================================================= */

const PROFILE = {
  name: 'Lokesh Pulivarthi',
  first: 'Lokesh',
  last: 'Pulivarthi',
  title: 'Software Engineer',
  location: 'United States',
  email: 'lokesh.pulivarthi@hotmail.com',
  linkedin: 'https://www.linkedin.com/in/lokeshpulivarthi/',
  github: 'https://github.com/LXP86050',
  resume: '/lokesh-pulivarthi-resume.pdf',
};

const EXPERIENCE = [
  {
    co: 'Infosys',
    role: 'Software Engineer II',
    when: '2024 — Present',
    blurb:
      'Shipping production retrieval-augmented systems on Azure OpenAI and AI Search. Architecting Azure AD and RBAC for enterprise data, replatforming Python services into a long-term .NET architecture, and tightening the seams of full-stack performance.',
  },
  {
    co: 'UCLA',
    role: 'Full-Stack Developer',
    when: '2022 — 2024',
    blurb:
      'Re-architected legacy ASP.NET into modern .NET Core with Entity Framework Core, Dapper, and dependency injection. Designed REST APIs, reduced database load on large datasets, and owned the front-end of internal tools used across the institution.',
  },
  {
    co: 'Cognizant',
    role: 'Full-Stack Developer',
    when: '2018 — 2021',
    blurb:
      'Built and maintained enterprise .NET Core and React applications. Integrated payment, messaging, and analytics services through secure APIs at production scale.',
  },
];

const PROJECTS = [
  {
    n: 'I',
    name: 'Enterprise RAG Knowledge Assistant',
    sub: 'Azure OpenAI · LangChain · Vector Search',
    body:
      'A grounded retrieval system serving more than eight thousand internal queries each week. Average latency held below one and a half seconds; token spend reduced by roughly a quarter.',
    architecture:
      'Hybrid retrieval (Azure AI Search BM25 + vector embeddings) → reranker → grounded generation with citations. LangChain orchestration, evaluation harness in LangSmith, response cache in Azure Cache for Redis, Azure AD with RBAC at the API edge.',
    tradeoff:
      "Chose hybrid retrieval over pure-vector because vector search fails on exact-match queries like 'open PO #12345' — the kind users actually type. The +12% retrieval cost is worth the recall on the queries that matter most.",
    metric: '−55%',
    metricLabel: 'analyst lookup time',
    stack: ['Python', 'LangChain', 'Azure OpenAI', 'Azure AI Search', 'REST', 'Azure AD'],
    year: '2025',
    image: '/diagrams/rag.png',
    imageAlt: 'Enterprise RAG query flow — user query through hybrid retrieval, reranker, Azure OpenAI, to a grounded answer with citations',
  },
  {
    n: 'II',
    name: 'Sentiment Analysis Platform',
    sub: 'Django · scikit-learn · PostgreSQL',
    body:
      'A real-time classifier for product reviews and customer feedback. Trained against a sizeable corpus to over ninety percent accuracy, served from a Django backend with a PostgreSQL analytics layer.',
    architecture:
      'scikit-learn + NLTK trained on 1.2M product reviews → Django REST API → Postgres analytics layer driving dashboards. Deployed on Azure for multi-tenant access.',
    tradeoff:
      'Chose scikit-learn over a fine-tuned LLM because the classical pipeline was ~10× faster and within 3% accuracy on this domain. The latency budget was the design constraint, not the F1 score.',
    metric: '>90%',
    metricLabel: 'classification accuracy',
    stack: ['Python', 'Django', 'scikit-learn', 'NLTK', 'PostgreSQL', 'Azure'],
    year: '2024',
  },
  {
    n: 'III',
    name: 'Agentic Compliance Workflow',
    sub: 'AI Agents · LangGraph · Multi-Agent Systems',
    body:
      'A multi-agent orchestration that routes documents through specialized reviewers — legal, financial, and technical agents — each with their own tools and grounded retrieval. Cut average compliance review from days to hours.',
    architecture:
      'LangGraph supervisor pattern. Coordinator agent routes documents to specialist agents by document type; each specialist owns its own tools and retriever. Persistent state in Postgres, function-calling for downstream actions, all turns logged to LangSmith for replay.',
    tradeoff:
      "Chose a supervisor over a swarm/peer pattern because handoffs are observable and a single agent's failure stops the workflow cleanly. Swarm felt elegant in the design doc; supervisor was the one that survived contact with the on-call rotation.",
    metric: '−70%',
    metricLabel: 'review cycle time',
    stack: ['LangGraph', 'LangChain', 'Azure OpenAI', 'Python', 'PostgreSQL', 'FastAPI'],
    year: '2025',
    image: '/diagrams/agents.png',
    imageAlt: 'Agentic compliance workflow — supervisor agent routing documents to legal, financial, and technical specialist agents, with shared state in Postgres and LangSmith replay',
  },
  {
    n: 'IV',
    name: 'LLM Evaluation & Observability Suite',
    sub: 'LLM Ops · Drift Detection · A/B Testing',
    body:
      'An evaluation harness wrapped around production LLM endpoints — golden datasets, automated drift detection, A/B testing for prompt variants. Catches roughly 40% more regressions in staging before they reach customers.',
    architecture:
      'Golden dataset of ~200 anchor queries + automated runner triggered on every PR + per-metric drift detector (groundedness, refusal rate, latency p95). Slack alerts on dips. Metrics piped to Datadog dashboards alongside infra signals so engineering can correlate model and platform regressions.',
    tradeoff:
      "Built our own harness over off-the-shelf evals because external evals don't know what 'correct' means for our domain. Domain-specific evals are 80% of the value; the harness scaffolding is the other 20%.",
    metric: '+40%',
    metricLabel: 'regressions caught pre-prod',
    stack: ['Python', 'LangSmith', 'Azure OpenAI', 'FastAPI', 'PostgreSQL', 'Datadog'],
    year: '2025',
    image: '/diagrams/evals.png',
    imageAlt: 'LLM evaluation harness — PR triggers golden-set runs against the staging endpoint, four metrics feed a drift detector, regressions block merge via Slack, trends flow to Datadog',
  },
  {
    n: 'V',
    name: 'Document Intelligence Pipeline',
    sub: 'Vision-Language Models · OCR · Schema Validation',
    body:
      'A multi-stage pipeline that ingests scanned forms, invoices, and contracts; extracts structured fields using vision-language models; validates against typed schemas; and feeds the data into downstream services.',
    architecture:
      "GPT-4 Vision extracts → typed schema validators reject low-confidence fields → human review queue for everything below threshold → confirmed fields flow into downstream services. Handles rotated scans, low contrast, and mixed handwriting.",
    tradeoff:
      'Chose human-in-the-loop over raising the confidence threshold because partial automation beats failed automation. Better to auto-handle 80% perfectly than 95% with occasional silent errors that take a month to catch.',
    metric: '−80%',
    metricLabel: 'manual data entry',
    stack: ['GPT-4 Vision', 'Azure AI Document Intelligence', 'Python', 'FastAPI', 'Azure Functions'],
    year: '2024',
    image: '/diagrams/vision.png',
    imageAlt: 'Document intelligence pipeline — scanned forms through GPT-4 Vision extraction and typed schema validation, split by confidence threshold into auto-processing or human review queue',
  },
  {
    n: 'VI',
    name: 'Python to .NET Replatforming',
    sub: 'Migration · Architecture · IaC',
    body:
      'Cross-cutting work to migrate Python services into a .NET-based architecture aligned with a long-term scalability roadmap. Touched application services, Bicep infrastructure, AKS, and the Azure DevOps pipelines.',
    architecture:
      'Strangler-fig pattern. New .NET services stood up behind a reverse proxy; traffic gradually shifted per-endpoint via routing rules; Python services retired endpoint-by-endpoint with no global cutover. Bicep modules per service, GitOps via Azure DevOps.',
    tradeoff:
      "Chose strangler-fig over a big-bang cutover because compliance required zero downtime and the team couldn't freeze Python development for a year-long migration. Slower in calendar time, far less risky in failure modes.",
    metric: '−30%',
    metricLabel: 'deployment time',
    stack: ['.NET Core', 'EF Core', 'Bicep', 'AKS', 'Azure DevOps'],
    year: '2024',
  },
];

const PRINCIPLES = [
  {
    n: '01',
    title: 'Evaluation before orchestration.',
    body:
      "The test harness ships before the prompt does. Prompts are the easiest thing to change; evals are the hardest — so I build them first. Most 'prompt engineering' is just iteration on the wrong axis.",
  },
  {
    n: '02',
    title: 'Latency is a feature.',
    body:
      "Sub-1.5s isn't a vanity metric — it's the line between 'magical' and 'broken' in the user's head. I optimize for the p95 tail, not the mean. Means lie about how the system feels.",
  },
  {
    n: '03',
    title: 'Grounding beats cleverness.',
    body:
      'A boring RAG that answers from the docs beats a clever prompt every time. The fanciness goes in retrieval — chunking, hybrid scoring, reranking — not in generation. The model is the last 10%.',
  },
  {
    n: '04',
    title: 'Boring tools, exotic problems.',
    body:
      "Postgres, .NET, React. The newer the stack, the older the team. I save the new-shiny budget for where it actually buys me something — and AI is where it actually does.",
  },
];

const NOTES = [
  {
    n: '01',
    title: 'Production RAG: the seven mistakes I made first.',
    abstract:
      'Chunking strategies that lose context. Vector-only retrieval that misses exact matches. Evals that score the wrong thing. The compound interest of small mistakes.',
    readTime: '8 min read',
    tags: ['RAG', 'Retrieval', 'Production', 'Evaluation'],
    outline: [
      'Chunk size — small chunks lose context, large chunks dilute relevance.',
      'Vector-only retrieval fails on exact-match queries (PO numbers, names, codes).',
      'Embeddings drift as the corpus updates — re-embed on a schedule, not on-demand.',
      'LLM-as-judge evals have biases (position, verbosity) you have to design around.',
      'Caching needs a key strategy that handles paraphrases without false hits.',
      'Citations and grounding signals must be designed in, not bolted on later.',
      'Hallucination prevention starts in retrieval, not in the generation step.',
    ],
  },
  {
    n: '02',
    title: 'LangGraph patterns for compliance workflows.',
    abstract:
      'Supervisor vs swarm, handoff design, where shared state lives, and when a single agent earns its keep over a multi-agent setup.',
    readTime: '6 min read',
    tags: ['Agents', 'LangGraph', 'Architecture', 'LLM Ops'],
    outline: [
      'Supervisor vs swarm — when each pattern earns its keep.',
      'Shared state in Postgres — serialization gotchas and what to checkpoint.',
      'Handoff design — when to escalate, when to continue, when to bail out.',
      'Function-calling with typed schemas as the agent-to-tool contract.',
      'Replay and audit trails — non-negotiable for compliance-domain agents.',
      'Failure modes — agent loops, infinite retries, runaway token spend.',
    ],
  },
  {
    n: '03',
    title: 'Evaluation harnesses that actually catch regressions.',
    abstract:
      'Golden datasets, drift detection, and the difference between unit tests and behaviour tests for LLM apps. Why off-the-shelf evals plateau and what to build instead.',
    readTime: '7 min read',
    tags: ['Evaluation', 'LLM Ops', 'Testing', 'Observability'],
    outline: [
      'Golden datasets — how to build them, how to version them, when to add to them.',
      'Per-metric tracking — groundedness, latency p95, refusal rate, token spend.',
      'LLM-as-judge — when it works, when it lies, and the prompts that make it honest.',
      'Drift detection on a moving target — corpus updates and model updates both shift the floor.',
      'CI integration — gates that catch regressions without blocking velocity.',
      'Alerting that survives the 3am page test — actionable, not noisy.',
    ],
  },
  {
    n: '04',
    title: 'Why I stopped optimizing prompts.',
    abstract:
      'The accidental discovery that retrieval quality dominates prompt cleverness. A 20-line prompt that beats a 200-line one because the context is right.',
    readTime: '5 min read',
    tags: ['RAG', 'Prompt Engineering', 'Retrieval'],
    outline: [
      'The discovery — retrieval quality dominates prompt cleverness on real workloads.',
      'Why prompt iteration plateaus after the first few rounds of tuning.',
      'Where the budget actually pays off — chunking, reranking, hybrid scoring, query rewriting.',
      'The 20-line prompt that beat a 200-line one (because the context was right).',
      'When prompts DO matter — output format, style, refusal behaviour.',
      'The replacement skill — writing good evaluators, not better prompts.',
    ],
  },
];

const STACK = [
  { group: 'Languages', items: ['Python', 'C#', 'TypeScript', 'JavaScript', 'SQL'] },
  { group: 'Backend', items: ['Django', 'FastAPI', '.NET Core', 'EF Core', 'Dapper'] },
  { group: 'Frontend', items: ['React', 'Fluent UI', 'Material UI'] },
  { group: 'Data & Search', items: ['PostgreSQL', 'SQL Server', 'Cosmos DB', 'Azure AI Search'] },
  { group: 'Cloud & DevOps', items: ['Azure', 'AKS', 'Docker', 'Bicep', 'CI / CD'] },
  { group: 'AI & ML', items: ['Azure OpenAI', 'GPT-4 Vision', 'LangChain', 'LangGraph', 'LangSmith', 'RAG', 'AI Agents', 'LLM Evaluation', 'scikit-learn', 'TensorFlow'] },
  { group: 'Security', items: ['Azure AD', 'OAuth', 'JWT', 'RBAC'] },
];

const NUMBERS = [
  { v: '6+', k: 'years shipping software' },
  { v: '8K', k: 'weekly retrieval queries' },
  { v: '3', k: 'companies' },
  { v: '−55%', k: 'analyst lookup time' },
];

const CERTS = [
  {
    n: '01',
    name: 'Introduction to Generative AI',
    issuer: 'Google Cloud Skills Boost',
    when: 'May 2026',
    url: 'https://www.skills.google/public_profiles/ae0bd8a6-6b75-4a9c-95a2-c07d5ef93f7e/badges/24345063',
  },
  {
    n: '02',
    name: 'Introduction to Large Language Models',
    issuer: 'Google Cloud Skills Boost',
    when: 'May 2026',
    url: 'https://www.skills.google/public_profiles/ae0bd8a6-6b75-4a9c-95a2-c07d5ef93f7e/badges/24351917',
  },
  {
    n: '03',
    name: 'Introduction to Responsible AI',
    issuer: 'Google Cloud Skills Boost',
    when: 'May 2026',
    url: 'https://www.skills.google/public_profiles/ae0bd8a6-6b75-4a9c-95a2-c07d5ef93f7e/badges/24352057',
  },
  {
    n: '04',
    name: 'Fundamentals of Machine Learning and Artificial Intelligence',
    issuer: 'AWS Training & Certification',
    when: 'May 2026',
    url: './aws-fundamentals-ml-ai.pdf',
  },
];

const HOBBIES = [
  {
    n: '01',
    title: 'Photography',
    body:
      "Travel, weddings, and editorial work — somewhere between hobby and side practice. Helps me see systems the way I see compositions: light, motion, and the in-between.",
  },
  {
    n: '02',
    title: 'Chess',
    body:
      "Daily blitz on Chess.com — partial to positional play, suspicious of sharp lines. The pattern-matching transfers to debugging better than I'd like to admit.",
  },
  {
    n: '03',
    title: 'Strength training',
    body:
      "Four days a week at the gym — push/pull/legs split, progressive overload, log every set. The compounding feels a lot like good engineering: small reps, kept honest, over a long time.",
  },
  {
    n: '04',
    title: 'Reading',
    body:
      "Non-fiction mostly — engineering blogs, biographies, AI papers. Currently working through Designing Data-Intensive Applications.",
  },
];

/* =========================================================
   ICONS — LinkedIn and GitHub as inline SVG (currentColor)
   ========================================================= */

/* =========================================================
   AMBIENT SECTION BACKGROUNDS
   ========================================================= */

/**
 * Subtle SVG illustration that sits behind a section's content at low
 * opacity. Each kind is themed — hobbies gets a coffee cup with rising
 * steam, contact gets a paper plane, etc.
 */
function SectionBg({ kind }) {
  return (
    <div className={`section-bg section-bg--${kind}`} aria-hidden>
      {kind === 'about' && (
        <svg viewBox="0 0 240 240" fill="none" stroke="currentColor" strokeWidth="2">
          <text
            x="40"
            y="200"
            fontFamily="Georgia, 'Times New Roman', serif"
            fontSize="280"
            fontWeight="400"
            fill="currentColor"
            stroke="none"
          >
            “
          </text>
        </svg>
      )}

      {kind === 'work' && (
        <svg viewBox="0 0 320 160" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          {/* </> */}
          <polyline points="60,40 20,80 60,120" />
          <line x1="160" y1="30" x2="120" y2="130" />
          <polyline points="220,40 260,80 220,120" />
        </svg>
      )}

      {kind === 'experience' && (
        <svg viewBox="0 0 400 60" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          {/* timeline ruler — long line + ticks */}
          <line x1="0" y1="30" x2="400" y2="30" />
          {Array.from({ length: 9 }).map((_, i) => {
            const x = 20 + i * 45;
            const tall = i % 2 === 0;
            return <line key={i} x1={x} y1={tall ? 12 : 22} x2={x} y2={tall ? 48 : 38} />;
          })}
        </svg>
      )}

      {kind === 'stack' && (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1.5">
          {/* atom — three orbits + nucleus */}
          <g className="section-bg__atom">
            <circle cx="100" cy="100" r="3" fill="currentColor" stroke="none" />
            <ellipse cx="100" cy="100" rx="90" ry="36" />
            <ellipse cx="100" cy="100" rx="90" ry="36" transform="rotate(60 100 100)" />
            <ellipse cx="100" cy="100" rx="90" ry="36" transform="rotate(120 100 100)" />
          </g>
        </svg>
      )}

      {kind === 'certs' && (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          {/* verified seal — outer rosette + inner circle + checkmark */}
          <g className="section-bg__seal">
            {Array.from({ length: 16 }).map((_, i) => {
              const angle = (i * 360) / 16;
              return (
                <line
                  key={i}
                  x1="100"
                  y1="20"
                  x2="100"
                  y2="34"
                  transform={`rotate(${angle} 100 100)`}
                />
              );
            })}
            <circle cx="100" cy="100" r="56" />
            <circle cx="100" cy="100" r="44" />
            <path d="M 80 100 L 95 116 L 122 86" strokeWidth="3" />
          </g>
        </svg>
      )}

      {kind === 'hobbies' && (
        <svg
          viewBox="0 0 200 240"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Steam — three wisps that rise & fade */}
          <path
            className="section-bg__steam section-bg__steam--1"
            d="M 70 60 Q 60 45 70 30 Q 80 15 70 0"
          />
          <path
            className="section-bg__steam section-bg__steam--2"
            d="M 100 60 Q 90 45 100 30 Q 110 15 100 0"
          />
          <path
            className="section-bg__steam section-bg__steam--3"
            d="M 130 60 Q 120 45 130 30 Q 140 15 130 0"
          />
          {/* Saucer */}
          <ellipse cx="100" cy="220" rx="78" ry="10" />
          <line x1="22" y1="220" x2="22" y2="222" />
          <line x1="178" y1="220" x2="178" y2="222" />
          {/* Mug body */}
          <path d="M 40 100 L 45 200 Q 47 215 65 215 L 130 215 Q 148 215 150 200 L 155 100 Z" />
          {/* Coffee surface */}
          <ellipse cx="97.5" cy="100" rx="57.5" ry="6" />
          {/* Handle */}
          <path d="M 155 130 Q 185 130 185 155 Q 185 180 155 175" />
        </svg>
      )}

      {kind === 'contact' && (
        <svg
          viewBox="0 0 240 200"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="section-bg__plane-wrap"
        >
          {/* Trailing dotted path */}
          <path
            d="M 10 170 Q 80 140 140 110"
            strokeDasharray="2 8"
            strokeWidth="1.5"
            opacity="0.6"
          />
          {/* Paper plane */}
          <g className="section-bg__plane">
            <path d="M 150 100 L 230 50 L 200 130 L 175 110 Z" />
            <path d="M 150 100 L 175 110 L 175 135 Z" />
            <line x1="150" y1="100" x2="200" y2="130" />
          </g>
        </svg>
      )}

      {kind === 'think' && (
        <svg viewBox="0 0 240 240" fill="none" stroke="currentColor" strokeWidth="1.4">
          {/* Concentric thinking-rings (target / radar) */}
          <circle cx="120" cy="120" r="112" />
          <circle cx="120" cy="120" r="84" />
          <circle cx="120" cy="120" r="56" />
          <circle cx="120" cy="120" r="28" />
          <circle cx="120" cy="120" r="3" fill="currentColor" stroke="none" />
          <line x1="0" y1="120" x2="240" y2="120" strokeDasharray="2 8" />
          <line x1="120" y1="0" x2="120" y2="240" strokeDasharray="2 8" />
        </svg>
      )}

      {kind === 'notes' && (
        <svg viewBox="0 0 280 200" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          {/* Sheet of paper with lines */}
          <path d="M 40 30 L 200 30 L 240 70 L 240 180 L 40 180 Z" />
          <path d="M 200 30 L 200 70 L 240 70" />
          {/* Text lines */}
          <line x1="60" y1="90" x2="220" y2="90" strokeDasharray="2 6" />
          <line x1="60" y1="108" x2="200" y2="108" strokeDasharray="2 6" />
          <line x1="60" y1="126" x2="220" y2="126" strokeDasharray="2 6" />
          <line x1="60" y1="144" x2="180" y2="144" strokeDasharray="2 6" />
          <line x1="60" y1="162" x2="210" y2="162" strokeDasharray="2 6" />
        </svg>
      )}
    </div>
  );
}

function LinkedInIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43c-1.14 0-2.07-.93-2.07-2.07s.93-2.07 2.07-2.07 2.07.93 2.07 2.07-.93 2.07-2.07 2.07zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

function GitHubIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 .3a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2.2c-3.3.7-4-1.4-4-1.4-.6-1.4-1.4-1.8-1.4-1.8-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1.1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.7-1.6-2.7-.3-5.5-1.3-5.5-6 0-1.2.5-2.3 1.3-3.1-.2-.4-.6-1.6 0-3.2 0 0 1-.3 3.4 1.2a11.5 11.5 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.7 1.6.2 2.8.1 3.2.8.8 1.3 1.9 1.3 3.1 0 4.6-2.8 5.6-5.5 5.9.5.4.9 1.2.9 2.5v3.5c0 .3.1.7.8.6A12 12 0 0 0 12 .3" />
    </svg>
  );
}

const NAV_LINKS = [
  { id: 'about', label: 'About' },
  { id: 'think', label: 'Approach' },
  { id: 'work', label: 'Work' },
  { id: 'experience', label: 'Experience' },
  { id: 'stack', label: 'Stack' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'notes', label: 'Notes' },
  { id: 'contact', label: 'Contact' },
];

/* Validation helpers — mirrored in /api/contact.js for server-side checks */
function isValidEmail(s) {
  if (typeof s !== 'string') return false;
  const v = s.trim();
  if (v.length < 6 || v.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
}

function isValidPhone(s) {
  if (typeof s !== 'string') return false;
  const digits = s.replace(/[^\d]/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

/* =========================================================
   HOOKS
   ========================================================= */

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function useTime() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function useScrolled(threshold = 24) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

/**
 * rAF-throttled scroll-Y reader.
 * Returns latest scroll position without spamming React re-renders.
 */
function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    let raf = 0;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      raf = requestAnimationFrame(() => {
        setY(window.scrollY);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  return y;
}

/**
 * Magnetic pull — element subtly chases the cursor when it's nearby.
 * Used on the primary CTA for an "Apple product page" premium feel.
 */
function useMagnetic(strength = 0.18) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Skip on touch/coarse pointers — pull is meaningless without hover
    if (window.matchMedia('(pointer: coarse)').matches) return;

    let raf = 0;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      el.style.transform = '';
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, [strength]);
  return ref;
}

/**
 * Animate a numeric value from 0 → target when the element enters the viewport.
 * `format` lets you keep the prefix/suffix (e.g. "8K", "−55%").
 */
function useCountUp(ref, raw, durationMs = 1400) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Parse the leading number out of strings like "8K", "6+", "−55%"
    const match = String(raw).match(/-?\d+(\.\d+)?/);
    if (!match) return; // nothing numeric — leave as-is
    const target = parseFloat(match[0]);
    const before = String(raw).slice(0, match.index);
    const after = String(raw).slice(match.index + match[0].length);
    let started = false;

    const render = (value) => {
      el.textContent = `${before}${Math.round(value)}${after}`;
    };
    render(0);

    const run = () => {
      const t0 = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - t0) / durationMs);
        // ease-out cubic — quick start, gentle settle (matches the rest of the site)
        const eased = 1 - Math.pow(1 - t, 3);
        render(target * eased);
        if (t < 1) requestAnimationFrame(step);
        else el.textContent = String(raw); // restore exact original (e.g. "−55%")
      };
      requestAnimationFrame(step);
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !started) {
            started = true;
            run();
            io.disconnect();
          }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, raw, durationMs]);
}

/* =========================================================
   SCROLL PROGRESS BAR
   ========================================================= */

function ScrollProgress() {
  const [w, setW] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const pct = total > 0 ? (window.scrollY / total) * 100 : 0;
      setW(pct);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="scroll-progress" style={{ transform: `scaleX(${w / 100})` }} />;
}

/* =========================================================
   NAV
   ========================================================= */

function Nav() {
  const scrolled = useScrolled(40);
  const [open, setOpen] = useState(false);

  // close mobile menu on resize up
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 920 && open) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  // lock scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
        <a href="#top" className="nav__brand" onClick={() => setOpen(false)}>
          Lokesh Pulivarthi
        </a>
        <div className="nav__links">
          {NAV_LINKS.map((l) => (
            <a key={l.id} href={`#${l.id}`}>{l.label}</a>
          ))}
        </div>
        <div className="nav__right">
          <a
            href={PROFILE.linkedin}
            target="_blank"
            rel="noreferrer"
            className="nav__icon"
            aria-label="LinkedIn"
            title="LinkedIn"
          >
            <LinkedInIcon />
          </a>
          <a
            href={PROFILE.github}
            target="_blank"
            rel="noreferrer"
            className="nav__icon"
            aria-label="GitHub"
            title="GitHub"
          >
            <GitHubIcon />
          </a>
          <a href={`mailto:${PROFILE.email}`} className="nav__cta">Get in touch</a>
          <button
            className={`nav__menu ${open ? 'nav__menu--open' : ''}`}
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <div className={`mobile-menu ${open ? 'mobile-menu--open' : ''}`} aria-hidden={!open}>
        {NAV_LINKS.map((l, i) => (
          <a
            key={l.id}
            href={`#${l.id}`}
            className="mobile-menu__link"
            style={{ '--i': i }}
            onClick={() => setOpen(false)}
          >
            {l.label}
          </a>
        ))}
        <a
          href={`mailto:${PROFILE.email}`}
          className="mobile-menu__cta"
          style={{ '--i': NAV_LINKS.length }}
          onClick={() => setOpen(false)}
        >
          Get in touch →
        </a>
        <div
          className="mobile-menu__socials"
          style={{ '--i': NAV_LINKS.length + 1 }}
        >
          <a
            href={PROFILE.linkedin}
            target="_blank"
            rel="noreferrer"
            aria-label="LinkedIn"
            onClick={() => setOpen(false)}
          >
            <LinkedInIcon size={20} />
            <span>LinkedIn</span>
          </a>
          <a
            href={PROFILE.github}
            target="_blank"
            rel="noreferrer"
            aria-label="GitHub"
            onClick={() => setOpen(false)}
          >
            <GitHubIcon size={20} />
            <span>GitHub</span>
          </a>
        </div>
      </div>
    </>
  );
}

/* =========================================================
   HERO
   ========================================================= */

function Hero() {
  const time = useTime();
  // Always show Pacific time — it's where I am, regardless of where the visitor is
  const t = time.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  });

  const heroRef = useRef(null);
  const primaryCtaRef = useMagnetic(0.16);
  const y = useScrollY();

  // Mouse-follow glow on hero (skipped on touch)
  useEffect(() => {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    const heroEl = heroRef.current;
    if (!heroEl) return;
    const onMove = (e) => {
      const rect = heroEl.getBoundingClientRect();
      if (
        e.clientY < rect.top || e.clientY > rect.bottom ||
        e.clientX < rect.left || e.clientX > rect.right
      ) return;
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const my = ((e.clientY - rect.top) / rect.height) * 100;
      heroEl.style.setProperty('--mx', `${x}%`);
      heroEl.style.setProperty('--my', `${my}%`);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Scroll-driven parallax + fade.
  // - Content shifts up gently (-y * 0.18)
  // - Content fades out as you scroll past
  // - Background glow shifts even slower for layered depth
  // - The two title words "tear apart" horizontally as you scroll down,
  //   and snap back to centre as you scroll up (re-tied to live scroll position)
  const vh = typeof window !== 'undefined' ? window.innerHeight || 800 : 800;
  const vw = typeof window !== 'undefined' ? window.innerWidth || 1200 : 1200;
  const heroFade = Math.max(0, 1 - y / (vh * 0.85));
  const heroLift = -y * 0.18;
  const glowLift = -y * 0.36;
  const xMax = Math.min(280, vw * 0.22);
  const xShift = Math.min(xMax, Math.max(0, y) * 0.55);
  const contentStyle = {
    transform: `translate3d(0, ${heroLift}px, 0)`,
    opacity: heroFade,
  };
  const glowStyle = {
    transform: `translate3d(0, ${glowLift}px, 0)`,
  };
  const leftWordStyle = {
    transform: `translate3d(${-xShift}px, 0, 0)`,
  };
  const rightWordStyle = {
    transform: `translate3d(${xShift}px, 0, 0)`,
  };

  return (
    <header className="hero" id="top" ref={heroRef}>
      <div className="hero__glow" style={glowStyle} aria-hidden />

      <div className="hero__content" style={contentStyle}>

      <div className="hero__pill" data-reveal>
        <span className="hero__pill-dot" />
        <span>Available for new opportunities</span>
      </div>

      <h1 className="hero__title">
        <span className="hero__title-slot" style={leftWordStyle}>
          <span className="hero__title-word" data-reveal style={{ '--reveal-delay': '0s' }}>
            Lokesh
          </span>
        </span>{' '}
        <span className="hero__title-slot" style={rightWordStyle}>
          <span className="hero__title-word" data-reveal style={{ '--reveal-delay': '0.12s' }}>
            Pulivarthi
          </span>
        </span>
      </h1>

      <p className="hero__deck" data-reveal style={{ '--reveal-delay': '0.24s' }}>
        I take AI features from notebook to on-call rotation. The
        interesting work is the eval harness, the retrieval pipeline,
        the auth seam — not the prompt.
      </p>

      <div className="hero__cta-row" data-reveal style={{ '--reveal-delay': '0.30s' }}>
        <a ref={primaryCtaRef} href="#work" className="btn btn--primary btn--magnetic">See selected work</a>
        <a
          href={PROFILE.resume}
          className="btn btn--ghost"
          download="Lokesh-Pulivarthi-Resume.pdf"
        >
          Download résumé
          <span className="btn__icon">↓</span>
        </a>
        <a href={`mailto:${PROFILE.email}`} className="btn btn--ghost">Email me</a>
      </div>

      <div className="hero__open" data-reveal style={{ '--reveal-delay': '0.36s' }}>
        <span className="hero__open-label">Open to</span>
        <span className="hero__open-roles">
          Senior AI Engineer · Senior Software Engineer · SDE II
        </span>
      </div>

      <div className="hero__meta" data-reveal style={{ '--reveal-delay': '0.42s' }}>
        <span>{PROFILE.location}</span>
        <span className="hero__meta-dot">·</span>
        <span>{t} · Pacific</span>
        <span className="hero__meta-dot">·</span>
        <span>6+ years shipping production</span>
      </div>

      </div> {/* /.hero__content */}
    </header>
  );
}

/* =========================================================
   SECTION HEAD
   ========================================================= */

function Head({ roman, title, kicker }) {
  return (
    <div className="head" data-reveal>
      {roman && (
        <div className="head__eyebrow">
          <span className="head__eyebrow-rule" />
          <span>{roman}</span>
        </div>
      )}
      <h2 className="head__title">{title}</h2>
      {kicker && <p className="head__kicker">{kicker}</p>}
    </div>
  );
}

/* =========================================================
   ABOUT
   ========================================================= */

function About() {
  return (
    <section className="about" id="about">
      <SectionBg kind="about" />
      <Head roman="I" title="About" kicker="A short note, in lieu of a portrait." />
      <div className="about__grid">
        <article className="about__lead" data-reveal>
          <p className="about__para about__para--lead">
            I'm Lokesh Pulivarthi — Lokesh P. if we're being brief, LP if
            we're being lazy. A software engineer who lives at the seam
            between AI and production systems: RAG pipelines, AI agents,
            LLM evaluation, and the full-stack apps they sit inside. I
            write the .NET service, the Django job, and the React UI on top
            — and I keep the Azure plumbing underneath honest.
          </p>
          <p className="about__para">
            Today I work as a Software Engineer II at Infosys, where my days
            split between RAG systems on Azure OpenAI, the Azure AD plumbing
            that keeps them honest, and the long migration of Python services
            into a calmer .NET architecture. Before that, full-stack roles at
            UCLA and Cognizant taught me that the best feature a system can
            ship is a quiet on-call rotation.
          </p>
          <p className="about__para">
            I'm based in the United States, and open to remote work anywhere
            a thoughtful team is building something durable.
          </p>
        </article>

        <aside className="about__side">
          {[
            { label: 'Currently', body: 'Software Engineer II, Infosys.' },
            {
              label: 'Studied',
              body: (
                <>
                  <span>M.S. Computer Science</span>
                  <span className="side__faint">University of Central Missouri</span>
                  <span style={{ marginTop: 8 }}>B.E. Electrical &amp; Electronics</span>
                  <span className="side__faint">SR University</span>
                </>
              ),
            },
            {
              label: 'Lives in',
              body: 'United States · open to remote',
            },
            { label: 'Writes about', body: 'Retrieval, evaluation, and the seams between languages.' },
          ].map((s, i) => (
            <div key={s.label} className="side" data-reveal style={{ '--reveal-delay': `${i * 0.06}s` }}>
              <div className="side__label">{s.label}</div>
              <div className="side__body">{s.body}</div>
            </div>
          ))}
        </aside>
      </div>
    </section>
  );
}

/* =========================================================
   HOW I THINK — engineering principles
   ========================================================= */

function HowIThink() {
  return (
    <section className="think" id="think">
      <SectionBg kind="think" />
      <Head
        roman="II"
        title="How I think"
        kicker="Four principles I keep coming back to."
      />
      <div className="think__grid">
        {PRINCIPLES.map((p, i) => (
          <article
            key={p.title}
            className="think__card"
            data-reveal
            style={{ '--reveal-delay': `${i * 0.06}s` }}
          >
            <div className="think__top">
              <span className="think__n">{p.n}</span>
            </div>
            <h3 className="think__title">{p.title}</h3>
            <p className="think__body">{p.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   WORK
   ========================================================= */

function Work() {
  return (
    <section className="work" id="work">
      <SectionBg kind="work" />
      <Head roman="III" title="Selected work" kicker="Six pieces, in roughly chronological reverse." />
      <div className="work__list">
        {PROJECTS.map((p, i) => (
          <Project key={p.n} p={p} i={i} />
        ))}
      </div>
    </section>
  );
}

function Project({ p, i }) {
  return (
    <article className="proj" data-reveal style={{ '--reveal-delay': `${i * 0.06}s` }}>
      <div className="proj__rule" />
      <div className="proj__grid">
        <div className="proj__lead">
          <div className="proj__top">
            <span className="proj__n">{p.n}</span>
            <span className="proj__year">{p.year}</span>
          </div>
          <h3 className="proj__name">
            {p.name}
            <span className="proj__name-arrow">→</span>
          </h3>
          <div className="proj__sub">{p.sub}</div>
        </div>

        <div className="proj__body-col">
          <p className="proj__body">{p.body}</p>
          {(p.architecture || p.tradeoff) && (
            <dl className="proj__details">
              {p.architecture && (
                <div className="proj__detail">
                  <dt className="proj__detail-k">Architecture</dt>
                  <dd className="proj__detail-v">{p.architecture}</dd>
                </div>
              )}
              {p.tradeoff && (
                <div className="proj__detail">
                  <dt className="proj__detail-k">Tradeoff</dt>
                  <dd className="proj__detail-v">{p.tradeoff}</dd>
                </div>
              )}
            </dl>
          )}
          <div className="proj__stack">
            {p.stack.map((s) => (
              <span key={s}>{s}</span>
            ))}
          </div>
        </div>

        <div className="proj__metric-col">
          <div className="proj__metric">{p.metric}</div>
          <div className="proj__metric-k">{p.metricLabel}</div>
        </div>
      </div>

      {p.image && (
        <figure className="proj__figure">
          <img
            src={p.image}
            alt={p.imageAlt || `${p.name} architecture diagram`}
            loading="lazy"
            className="proj__image"
          />
          <figcaption className="proj__figcaption">
            Architecture diagram · {p.name}
          </figcaption>
        </figure>
      )}
    </article>
  );
}

/* =========================================================
   NUMBERS
   ========================================================= */

function Numbers() {
  return (
    <section className="nums">
      <div className="nums__grid">
        {NUMBERS.map((s, i) => (
          <NumberCell key={i} value={s.v} label={s.k} index={i} />
        ))}
      </div>
    </section>
  );
}

function NumberCell({ value, label, index }) {
  const ref = useRef(null);
  useCountUp(ref, value);
  return (
    <div className="nums__cell" data-reveal style={{ '--reveal-delay': `${index * 0.08}s` }}>
      <div className="nums__v" ref={ref}>{value}</div>
      <div className="nums__k">{label}</div>
    </div>
  );
}

/* =========================================================
   EXPERIENCE
   ========================================================= */

function Experience() {
  return (
    <section className="exp" id="experience">
      <SectionBg kind="experience" />
      <Head roman="IV" title="Experience" kicker="A short table of contents." />
      <ul className="exp__list">
        {EXPERIENCE.map((e, i) => (
          <li key={e.co} className="exp__row" data-reveal style={{ '--reveal-delay': `${i * 0.08}s` }}>
            <div className="exp__when">{e.when}</div>
            <div className="exp__main">
              <h3 className="exp__co">
                {e.co}
                {i === 0 && <span className="exp__live">Now</span>}
              </h3>
              <div className="exp__role">{e.role}</div>
              <p className="exp__blurb">{e.blurb}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* =========================================================
   STACK
   ========================================================= */

function Stack() {
  return (
    <section className="stack" id="stack">
      <SectionBg kind="stack" />
      <Head roman="V" title="Stack" kicker="A loose taxonomy of the tools I reach for." />
      <div className="stack__list">
        {STACK.map((g, i) => (
          <div key={g.group} className="stack__row" data-reveal style={{ '--reveal-delay': `${i * 0.05}s` }}>
            <div className="stack__label">{g.group}</div>
            <div className="stack__items">
              {g.items.map((s, j) => (
                <span key={s} className="stack__item">
                  {s}
                  {j < g.items.length - 1 && <span className="stack__sep">·</span>}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   CREDENTIALS — public, verifiable badges and certificates
   ========================================================= */

function Credentials() {
  return (
    <section className="certs" id="certifications">
      <SectionBg kind="certs" />
      <Head
        roman="VI"
        title="Certifications"
        kicker="Course-completion certificates and badges. Public links back to the issuer where available."
      />
      <ul className="certs__list">
        {CERTS.map((c, i) => (
          <li
            key={c.name}
            className="certs__row"
            data-reveal
            style={{ '--reveal-delay': `${i * 0.06}s` }}
          >
            <div className="certs__n">{c.n}</div>
            <div className="certs__main">
              <h3 className="certs__name">{c.name}</h3>
              <div className="certs__meta">
                <span className="certs__issuer">{c.issuer}</span>
                <span className="certs__dot" aria-hidden>·</span>
                <span className="certs__when">{c.when}</span>
              </div>
            </div>
            {c.url ? (
              <a
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="certs__verify"
                aria-label={`Verify ${c.name} on ${c.issuer}`}
              >
                Verify <span aria-hidden>↗</span>
              </a>
            ) : (
              <span className="certs__verify certs__verify--none" aria-hidden>
                On certificate
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* =========================================================
   NOTES — writing / engineering blog stubs
   ========================================================= */

function NotesSection() {
  return (
    <section className="notes-sec" id="notes">
      <SectionBg kind="notes" />
      <Head
        roman="VII"
        title="Notes"
        kicker="Writing about RAG, agents, and the seams between them."
      />
      <ol className="notes-sec__list">
        {NOTES.map((n, i) => (
          <li
            key={n.title}
            className="notes-sec__item"
            data-reveal
            style={{ '--reveal-delay': `${i * 0.06}s` }}
            tabIndex={0}
          >
            <div className="notes-sec__top">
              <span className="notes-sec__n">{n.n}</span>
            </div>
            <h3 className="notes-sec__title">{n.title}</h3>
            <p className="notes-sec__abstract">{n.abstract}</p>

            <div className="notes-sec__expanded">
              <div className="notes-sec__meta">
                {n.readTime && (
                  <span className="notes-sec__readtime">{n.readTime}</span>
                )}
                {n.tags && n.tags.length > 0 && (
                  <span className="notes-sec__tags">
                    {n.tags.map((t) => (
                      <span key={t} className="notes-sec__tag">{t}</span>
                    ))}
                  </span>
                )}
              </div>
              {n.outline && n.outline.length > 0 && (
                <>
                  <div className="notes-sec__outline-label">In this piece</div>
                  <ul className="notes-sec__outline">
                    {n.outline.map((point, j) => (
                      <li key={j}>{point}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className="notes-sec__hint" aria-hidden>
              <span>Hover for details</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 7h8M7 3l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* =========================================================
   HOBBIES
   ========================================================= */

function Hobbies() {
  return (
    <section className="hobbies" id="hobbies">
      <SectionBg kind="hobbies" />
      <Head roman="VIII" title="Off the clock" kicker="What I'm into when I'm not at a terminal." />
      <div className="hobbies__grid">
        {HOBBIES.map((h, i) => (
          <article
            key={h.n}
            className="hobby"
            data-reveal
            style={{ '--reveal-delay': `${i * 0.08}s` }}
          >
            <div className="hobby__top">
              <span className="hobby__n">{h.n}</span>
              <span className="hobby__title">{h.title}</span>
            </div>
            <p className="hobby__body">{h.body}</p>
            {h.link && (
              <a
                href={h.link}
                target="_blank"
                rel="noreferrer"
                className="hobby__link"
              >
                {h.linkLabel || h.link} <span aria-hidden>↗</span>
              </a>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   CONTACT
   ========================================================= */

function Contact() {
  return (
    <section className="contact" id="contact">
      <SectionBg kind="contact" />
      <Head roman="IX" title="Get in touch" kicker="The fastest path is email; I read everything." />

      <div className="contact__inner" data-reveal>
        <a href={`mailto:${PROFILE.email}`} className="contact__big">
          Let's build something durable.
          <span className="contact__big-arrow">→</span>
        </a>

        <div className="contact__rows">
          {[
            { k: 'Email', v: PROFILE.email, href: `mailto:${PROFILE.email}` },
            { k: 'Résumé', v: 'Lokesh-Pulivarthi-Resume.pdf', href: PROFILE.resume, download: true },
            { k: 'GitHub', v: 'github.com/LXP86050', href: PROFILE.github, ext: true },
            { k: 'LinkedIn', v: 'linkedin.com/in/lokeshpulivarthi', href: PROFILE.linkedin, ext: true },
            { k: 'Location', v: `${PROFILE.location} · open to remote` },
          ].map((r, i) => (
            <CRow key={r.k} {...r} delay={i * 0.06} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CRow({ k, v, href, ext, download, delay }) {
  const Wrap = href ? 'a' : 'div';
  const props = href
    ? {
        href,
        ...(ext ? { target: '_blank', rel: 'noreferrer' } : {}),
        ...(download ? { download: 'Lokesh-Pulivarthi-Resume.pdf' } : {}),
      }
    : {};
  const arrow = download ? '↓' : ext ? '↗' : '→';
  return (
    <Wrap className="crow" data-reveal style={{ '--reveal-delay': `${delay}s` }} {...props}>
      <span className="crow__k">{k}</span>
      <span className="crow__v">{v}</span>
      {href && <span className="crow__arrow">{arrow}</span>}
    </Wrap>
  );
}

/* =========================================================
   FOOTER
   ========================================================= */

function Footer() {
  return (
    <footer className="foot">
      <div className="foot__socials">
        <a
          href={PROFILE.linkedin}
          target="_blank"
          rel="noreferrer"
          className="foot__social"
          aria-label="LinkedIn"
        >
          <span className="foot__social-icon"><LinkedInIcon size={18} /></span>
          <span>LinkedIn</span>
          <span className="foot__social-arrow" aria-hidden>↗</span>
        </a>
        <a
          href={PROFILE.github}
          target="_blank"
          rel="noreferrer"
          className="foot__social"
          aria-label="GitHub"
        >
          <span className="foot__social-icon"><GitHubIcon size={18} /></span>
          <span>GitHub</span>
          <span className="foot__social-arrow" aria-hidden>↗</span>
        </a>
        <a
          href={`mailto:${PROFILE.email}`}
          className="foot__social"
          aria-label="Email"
        >
          <span className="foot__social-icon" aria-hidden>✉</span>
          <span>Email</span>
          <span className="foot__social-arrow" aria-hidden>→</span>
        </a>
      </div>
      <div className="foot__row">
        <span>© 2018 Lokesh Pulivarthi.</span>
        <span className="foot__cred">Built in {PROFILE.location}.</span>
        <a href="#top" className="foot__top">Back to top</a>
      </div>
    </footer>
  );
}

/* =========================================================
   DROP A NOTE — inline contact form, POSTs to /api/contact
   ========================================================= */

function DropNote() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    _hp: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [serverError, setServerError] = useState('');

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    if (status === 'error') setStatus('idle');
  };

  const validate = () => {
    const errs = {};
    const msg = form.message.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();

    if (msg.length < 5) {
      errs.message = 'Please write a few more words.';
    } else if (msg.length > 2000) {
      errs.message = 'Keep it under 2,000 characters.';
    }

    if (!email && !phone) {
      errs.contact = 'Add an email or phone so I can reply.';
    } else {
      if (email && !isValidEmail(email)) {
        errs.email = 'Please enter a valid email.';
      }
      if (phone && !isValidPhone(phone)) {
        errs.phone = 'Please enter a valid phone number.';
      }
    }
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setStatus('sending');
    setServerError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus('error');
        setServerError(
          data?.error ||
            'Something went wrong. Try emailing lokesh.pulivarthi@hotmail.com directly.'
        );
        return;
      }
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', message: '', _hp: '' });
    } catch (err) {
      setStatus('error');
      setServerError(
        'Network hiccup. Try emailing lokesh.pulivarthi@hotmail.com directly.'
      );
    }
  };

  const isSending = status === 'sending';
  const isSent = status === 'sent';

  return (
    <section className="note" id="note">
      <Head
        roman="X"
        title="Drop a note"
        kicker="Faster than email if you're already here. Goes straight to my inbox."
      />

      <form className="note__form" onSubmit={onSubmit} noValidate>
        {/* Honeypot — bots fill it, humans never see it */}
        <input
          type="text"
          name="company"
          tabIndex={-1}
          autoComplete="off"
          className="note__hp"
          value={form._hp}
          onChange={set('_hp')}
          aria-hidden="true"
        />

        <div className="note__row">
          <label className="note__field">
            <span className="note__label">Name <span className="note__opt">(optional)</span></span>
            <input
              type="text"
              autoComplete="name"
              maxLength={100}
              className="note__input"
              value={form.name}
              onChange={set('name')}
              disabled={isSending || isSent}
              placeholder="Your name"
            />
          </label>
        </div>

        <div className="note__row note__row--split">
          <label className="note__field">
            <span className="note__label">Email</span>
            <input
              type="email"
              autoComplete="email"
              maxLength={254}
              className={`note__input ${errors.email ? 'note__input--err' : ''}`}
              value={form.email}
              onChange={set('email')}
              disabled={isSending || isSent}
              placeholder="you@company.com"
              aria-invalid={!!errors.email}
            />
            {errors.email && <span className="note__err">{errors.email}</span>}
          </label>
          <label className="note__field">
            <span className="note__label">Phone</span>
            <input
              type="tel"
              autoComplete="tel"
              maxLength={30}
              className={`note__input ${errors.phone ? 'note__input--err' : ''}`}
              value={form.phone}
              onChange={set('phone')}
              disabled={isSending || isSent}
              placeholder="+1 555 555 5555"
              aria-invalid={!!errors.phone}
            />
            {errors.phone && <span className="note__err">{errors.phone}</span>}
          </label>
        </div>
        {errors.contact && !errors.email && !errors.phone && (
          <div className="note__err note__err--row">{errors.contact}</div>
        )}

        <div className="note__row">
          <label className="note__field">
            <span className="note__label">Message</span>
            <textarea
              rows={5}
              maxLength={2000}
              className={`note__input note__textarea ${errors.message ? 'note__input--err' : ''}`}
              value={form.message}
              onChange={set('message')}
              disabled={isSending || isSent}
              placeholder="Tell me what you're working on, what you'd like to chat about, or just say hi."
              aria-invalid={!!errors.message}
            />
            <div className="note__count">
              {errors.message ? (
                <span className="note__err">{errors.message}</span>
              ) : (
                <span className="note__counter">{form.message.length} / 2000</span>
              )}
            </div>
          </label>
        </div>

        <div className="note__foot">
          <button
            type="submit"
            className="note__send"
            disabled={isSending || isSent}
          >
            {isSent ? 'Sent ✓' : isSending ? 'Sending…' : 'Send note →'}
          </button>
          <div className="note__status" role="status" aria-live="polite">
            {isSent && (
              <span className="note__ok">Got it. I usually reply within a day or two.</span>
            )}
            {status === 'error' && serverError && (
              <span className="note__err">{serverError}</span>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}

/* =========================================================
   LOKESH'S AI — chat widget grounded on resume + site context
   ========================================================= */

const AI_SUGGESTIONS = [
  "What is Lokesh working on now?",
  'Walk me through the RAG project',
  'Why hire Lokesh?',
  "What is his stack?",
];

function LokeshAi() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hi — I'm Lokesh's AI. Ask me about his experience, stack, projects, or anything you'd want to know before reaching out.",
    },
  ]);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Autoscroll on new messages / loading state
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  // Autofocus input when opened
  useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 320);
      return () => clearTimeout(id);
    }
  }, [open]);

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: next.map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      const reply =
        data?.message ||
        "I'm not sure — best to ask Lokesh directly at lokesh.pulivarthi@hotmail.com.";
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Network hiccup on my end. Try again in a moment, or email lokesh.pulivarthi@hotmail.com.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Close on Esc when open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        className={`ai-fab ${open ? 'ai-fab--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close Lokesh's AI" : "Open Lokesh's AI"}
        aria-expanded={open}
      >
        <span className="ai-fab__icon" aria-hidden>
          {open ? '×' : '✦'}
        </span>
        <span className="ai-fab__label">{open ? 'Close' : "Ask Lokesh's AI"}</span>
      </button>

      <div className={`ai-panel ${open ? 'ai-panel--open' : ''}`} aria-hidden={!open}>
        <div className="ai-head">
          <div className="ai-head__title">
            <span className="ai-head__dot" />
            <strong>Lokesh&rsquo;s AI</strong>
          </div>
          <button
            className="ai-head__close"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="ai-body" ref={bodyRef}>
          {messages.map((m, i) => (
            <div key={i} className={`ai-msg ai-msg--${m.role}`}>
              {m.role === 'assistant' && <span className="ai-msg__who">Lokesh&rsquo;s AI</span>}
              <p>{m.content}</p>
            </div>
          ))}
          {loading && (
            <div className="ai-msg ai-msg--assistant ai-msg--loading">
              <span className="ai-msg__who">Lokesh&rsquo;s AI</span>
              <p>
                <span className="ai-dots">
                  <i /><i /><i />
                </span>
                thinking…
              </p>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="ai-chips">
            {AI_SUGGESTIONS.map((s) => (
              <button key={s} className="ai-chip" onClick={() => send(s)} disabled={loading}>
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          className="ai-form"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <input
            ref={inputRef}
            className="ai-input"
            placeholder="Ask anything about Lokesh…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={800}
            disabled={loading}
          />
          <button
            type="submit"
            className="ai-send"
            disabled={loading || !input.trim()}
            aria-label="Send message"
          >
            ↑
          </button>
        </form>
      </div>
    </>
  );
}

/* =========================================================
   APP
   ========================================================= */

export default function App() {
  useReveal();
  return (
    <div className="app">
      <ScrollProgress />
      <Nav />
      <main>
        <Hero />
        <About />
        <HowIThink />
        <Work />
        <Numbers />
        <Experience />
        <Stack />
        <Credentials />
        <NotesSection />
        <Hobbies />
        <Contact />
        <DropNote />
      </main>
      <Footer />
      <LokeshAi />
    </div>
  );
}
